import { API_CONFIG, DEFAULT_CREDENTIALS } from '../../config/api';
import authService from '../auth';

export interface NFTMetadata {
  identifier: string;
  collection: string;
  contract: string;
  token_standard: string;
  name: string;
  description: string;
  image_url: string;
  display_image_url: string;
  display_animation_url: string | null;
  metadata_url: string;
  opensea_url: string;
  updated_at: string;
  is_disabled: boolean;
  is_nsfw: boolean;
}

export interface Offer {
  order_hash: string;
  created_date: string;
  closing_date: string;
  expiration_time: number;
  current_price: string;
  maker: { address: string; profile_img_url: string | null };
  side: string;
  order_type: string;
  cancelled: boolean;
  finalized: boolean;
  token_id: string;
}

class NFTService {
  private cache: NFTMetadata[] | null = null;
  private cacheExpiry: number | null = null;
  private offersCache: Map<string, { offers: Offer[]; expiry: number }> = new Map();
  private accountNFTsCache: Map<string, { nfts: NFTMetadata[]; expiry: number }> = new Map();
  private nftOwnerCache: Map<string, { owner: { ownerWalletAddress: string } | null; expiry: number }> = new Map();
  private authAttempted: boolean = false;

  async getNFTs(imageWidth: number = 1000, refresh: boolean = false): Promise<NFTMetadata[]> {
    if (!refresh && this.cache && this.cacheExpiry && Date.now() < this.cacheExpiry) {
      return this.cache;
    }

    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        await this.ensureAuth();
        
        if (!authService.isAuthenticated()) {
          // Retry authentication on first attempt
          if (attempt === 0) {
            this.authAttempted = false; // Reset auth attempt flag
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1))); // Wait before retry
            continue;
          }
          console.warn('Authentication failed after retries, returning empty NFT list');
          return [];
        }

        const token = await authService.getValidToken();
        if (!token) {
          throw new Error('Failed to get authentication token');
        }

        const refreshParam = refresh ? '&refresh=true' : '';
        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.OPENSEA_NFTS}?imageWidth=${imageWidth}${refreshParam}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          const nfts = this.transformResponse(data);
          
          // Only cache if we got valid NFTs
          if (nfts.length > 0) {
            this.cache = nfts;
            this.cacheExpiry = Date.now() + 5 * 60 * 1000; // 5 min cache
          }
          
          return nfts;
        } else {
          // Log response error details
          const errorText = await response.text().catch(() => 'Unable to read error response');
          lastError = new Error(`API returned ${response.status}: ${errorText}`);
          
          // Don't retry on client errors (4xx), but retry on server errors (5xx) and network issues
          if (response.status >= 400 && response.status < 500) {
            console.error(`Client error (${response.status}):`, errorText);
            break; // Don't retry client errors
          }
          
          // Retry on server errors
          if (attempt < maxRetries - 1) {
            const delay = 1000 * Math.pow(2, attempt); // Exponential backoff: 1s, 2s, 4s
            console.warn(`API request failed (attempt ${attempt + 1}/${maxRetries}), retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
        }
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        console.error(`API error (attempt ${attempt + 1}/${maxRetries}):`, lastError);
        
        // Retry on network errors
        if (attempt < maxRetries - 1) {
          const delay = 1000 * Math.pow(2, attempt); // Exponential backoff: 1s, 2s, 4s
          console.warn(`Network error, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
      }
    }
    
    // If we have cached data, return it even if fresh fetch failed
    if (this.cache && this.cache.length > 0) {
      console.warn('Using cached NFTs due to API failure:', lastError?.message);
      return this.cache;
    }
    
    console.error('Failed to fetch NFTs after all retries:', lastError?.message);
    return [];
  }

  private async ensureAuth(): Promise<void> {
    if (this.authAttempted || authService.isAuthenticated()) return;
    
    this.authAttempted = true;
    
    // Skip authentication if no credentials are provided
    if (!DEFAULT_CREDENTIALS.username || !DEFAULT_CREDENTIALS.password) {
      console.warn('No API credentials provided. Set REACT_APP_API_USERNAME and REACT_APP_API_PASSWORD environment variables.');
      return;
    }
    
    try {
      await authService.login(DEFAULT_CREDENTIALS.username, DEFAULT_CREDENTIALS.password);
    } catch (error) {
      if (error instanceof Error && error.message.includes('429')) {
        console.warn('⏳ Rate limited - too many login attempts. Please wait before trying again.');
      } else {
        console.error('❌ Authentication failed:', error);
      }
    }
  }

  private transformResponse(data: any): NFTMetadata[] {
    const allNFTs: NFTMetadata[] = [];
    
    if (data.collections) {
      Object.entries(data.collections).forEach(([collectionKey, collection]: [string, any]) => {
        const nfts = collection.nfts || [];
        const contractAddress = collection.contract_address || "0x2ff689da40ebd089caccb57ad61bb4c75df5ee1b";
        nfts.forEach((nft: any) => {
          allNFTs.push({
            identifier: nft.identifier,
            collection: nft.collection_slug || collectionKey,
            contract: contractAddress,
            token_standard: "erc1155",
            name: nft.name,
            description: nft.description,
            image_url: nft.display_image_url || nft.image_url,
            display_image_url: nft.display_image_url,
            display_animation_url: null,
            metadata_url: `ipfs://metadata/${nft.identifier}`,
            opensea_url: `https://opensea.io/assets/ethereum/${contractAddress}/${nft.identifier}`,
            updated_at: new Date().toISOString(),
            is_disabled: false,
            is_nsfw: false
          });
        });
      });
    }

    return allNFTs;
  }

  async getOffersByNFT(contractAddress: string, tokenId: string, refresh: boolean = false): Promise<Offer[]> {
    const cacheKey = `${contractAddress}-${tokenId}`;
    
    // Check cache if not refreshing
    if (!refresh) {
      const cached = this.offersCache.get(cacheKey);
      if (cached && Date.now() < cached.expiry) {
        return cached.offers;
      }
    }

    try {
      await this.ensureAuth();
      
      if (!authService.isAuthenticated()) {
        console.warn('Authentication failed, returning empty offers list');
        return [];
      }

      const token = await authService.getValidToken();
      const refreshParam = refresh ? '&refresh=true' : '';
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.OPENSEA_OFFERS}?asset_contract_address=${contractAddress}&token_ids=${tokenId}${refreshParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const offers = this.transformOffers(data.orders || []);
        
        // Cache the results for 2 minutes (offers change frequently)
        this.offersCache.set(cacheKey, {
          offers,
          expiry: Date.now() + 2 * 60 * 1000
        });
        
        return offers;
      }
    } catch (error) {
      console.error('Offers API error:', error);
    }

    return [];
  }

  clearCache(): void {
    this.cache = null;
    this.cacheExpiry = null;
    this.accountNFTsCache.clear();
    this.nftOwnerCache.clear();
  }

  private transformOffers(orders: any[]): Offer[] {
    return orders.map((order: any) => ({
      order_hash: order.order_hash,
      created_date: order.created_date,
      closing_date: order.closing_date,
      expiration_time: order.expiration_time,
      current_price: order.current_price,
      maker: order.maker,
      side: order.side,
      order_type: order.order_type,
      cancelled: order.cancelled,
      finalized: order.finalized,
      token_id: order.taker_asset_bundle?.assets?.[0]?.token_id || ''
    }));
  }

  // Hybrid approach: Frontend signs, API submits
  async submitOffer(signedOrder: {
    parameters: any;
    signature: string;
    protocol_address: string;
  }): Promise<{ success: boolean; message?: string; orderHash?: string; error?: string }> {
    try {
      await this.ensureAuth();
      
      if (!authService.isAuthenticated()) {
        return {
          success: false,
          error: 'Authentication required to submit offer'
        };
      }

      const token = await authService.getValidToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.OPENSEA_OFFER_SUBMIT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(signedOrder)
      });

      const result = await response.json();
      
      if (result.success) {
        return {
          success: true,
          message: result.message,
          orderHash: result.orderHash
        };
      } else {
        return {
          success: false,
          error: result.message || result.error || 'Failed to submit offer'
        };
      }
    } catch (error) {
      console.error('Error submitting offer:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to submit offer'
      };
    }
  }

  async getNFTsByAccount(accountAddress: string, chain: string = 'ethereum', limit: number = 100, refresh: boolean = false): Promise<NFTMetadata[]> {
    const cacheKey = `${accountAddress}-${chain}-${limit}`;
    
    // Check cache first if not refreshing
    if (!refresh) {
      const cached = this.accountNFTsCache.get(cacheKey);
      if (cached && Date.now() < cached.expiry) {
        return cached.nfts;
      }
    }

    try {
      await this.ensureAuth();
      
      if (!authService.isAuthenticated()) {
        console.warn('Authentication failed, returning empty NFTs list');
        return [];
      }

      const token = await authService.getValidToken();
      const refreshParam = refresh ? '&refresh=true' : '';
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.OPENSEA_ACCOUNT_NFTS}/${chain}/account/${accountAddress}/nfts?limit=${limit}${refreshParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const nfts = this.transformAccountNFTsResponse(data.nfts || []);
        
        this.accountNFTsCache.set(cacheKey, {
          nfts,
          expiry: Date.now() + 5 * 60 * 1000
        });
        
        return nfts;
      }
    } catch (error) {
      console.error('Account NFTs API error:', error);
    }

    return [];
  }

  private transformAccountNFTsResponse(nfts: any[]): NFTMetadata[] {
    return nfts
      .filter(nft => {
        // Filter out ENS collections
        const collectionName = nft.collection?.toLowerCase() || '';
        const nftName = nft.name?.toLowerCase() || '';
        
        const ensTerms = [
          'ens',
          'ethereum name service',
          'ethereum name',
          'name service',
          '.eth',
          'ens domain',
          'ethereum domain'
        ];
        
        const isENS = ensTerms.some(term => 
          collectionName.includes(term) || nftName.includes(term)
        );
        
        return !isENS;
      })
      .map((nft: any) => ({
        identifier: nft.identifier,
        collection: nft.collection,
        contract: nft.contract,
        token_standard: nft.token_standard,
        name: nft.name,
        description: nft.description,
        image_url: nft.display_image_url || nft.image_url,
        display_image_url: nft.display_image_url,
        display_animation_url: nft.display_animation_url,
        metadata_url: nft.metadata_url,
        opensea_url: nft.opensea_url,
        updated_at: nft.updated_at,
        is_disabled: nft.is_disabled,
        is_nsfw: nft.is_nsfw
      }));
  }

  async getNFTOwner(contractAddress: string, tokenId: string, refresh: boolean = false): Promise<{ ownerWalletAddress: string } | null> {
    const cacheKey = `${contractAddress}-${tokenId}`;
    
    // Check cache first if not refreshing
    if (!refresh) {
      const cached = this.nftOwnerCache.get(cacheKey);
      if (cached && Date.now() < cached.expiry) {
        return cached.owner;
      }
    }

    try {
      await this.ensureAuth();
      
      if (!authService.isAuthenticated()) {
        console.warn('Authentication failed, cannot fetch NFT owner');
        return null;
      }

      const token = await authService.getValidToken();
      const refreshParam = refresh ? '?refresh=true' : '';
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.OPENSEA_NFT_OWNER}/${contractAddress}/${tokenId}${refreshParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const owner = { ownerWalletAddress: data.ownerWalletAddress };
        
        // Cache the results for 10 minutes (NFT ownership changes infrequently)
        this.nftOwnerCache.set(cacheKey, {
          owner,
          expiry: Date.now() + 10 * 60 * 1000
        });
        
        return owner;
      }
    } catch (error) {
      console.error('NFT Owner API error:', error);
    }

    return null;
  }

  clearOffersCache(contractAddress?: string, tokenId?: string): void {
    if (contractAddress && tokenId) {
      // Clear cache for specific NFT
      const cacheKey = `${contractAddress}-${tokenId}`;
      this.offersCache.delete(cacheKey);
    } else {
      // Clear all offers cache
      this.offersCache.clear();
    }
  }
}

const nftService = new NFTService();
export default nftService;
